import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, CircularProgress, Container, Typography } from '@mui/material';
import { CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useSubscription } from '../../contexts/SubscriptionContext';

type PaymentStatus = 'processing' | 'success' | 'error' | 'cancelled';

const PaymentPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { executePayment } = useSubscription();
  const [status, setStatus] = useState<PaymentStatus>('processing');
  const [message, setMessage] = useState<string>('Processing your payment...');

  useEffect(() => {
    const processPayment = async () => {
      const params = new URLSearchParams(location.search);
      const paymentId = params.get('paymentId');
      const PayerID = params.get('PayerID');

      if (!paymentId || !PayerID) {
        setStatus('error');
        setMessage('Invalid payment parameters. Please try again.');
        return;
      }

      try {
        const result = await executePayment(paymentId, PayerID);
        if (result) {
          setStatus('success');
          setMessage('Payment completed successfully! Redirecting to dashboard...');
          // Redirect to dashboard after 3 seconds
          setTimeout(() => navigate('/instructor/dashboard'), 3000);
        } else {
          throw new Error('Payment execution failed');
        }
      } catch (error) {
        console.error('Payment error:', error);
        setStatus('error');
        setMessage('Failed to process payment. Please try again or contact support.');
      }
    };

    processPayment();
  }, [location.search, navigate, executePayment]);

  const renderStatusIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircle color="success" sx={{ fontSize: 60, mb: 2 }} />;
      case 'error':
      case 'cancelled':
        return <ErrorIcon color="error" sx={{ fontSize: 60, mb: 2 }} />;
      default:
        return <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />;
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Card sx={{ width: '100%', maxWidth: 500, p: 4, textAlign: 'center' }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
            {renderStatusIcon()}
            <Typography variant="h5" component="h1" gutterBottom>
              {status === 'success' 
                ? 'Payment Successful!' 
                : status === 'error' 
                  ? 'Payment Failed'
                  : 'Processing Payment...'}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {message}
            </Typography>
            
            {(status === 'error' || status === 'cancelled') && (
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => navigate('/instructor/subscribe')}
                sx={{ mt: 3 }}
              >
                Try Again
              </Button>
            )}
            
            {status === 'success' && (
              <Button 
                variant="outlined" 
                onClick={() => navigate('/instructor/dashboard')}
                sx={{ mt: 3 }}
              >
                Go to Dashboard
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default PaymentPage;
